import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AppointmentsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    doctor_id: '',
    requested_date: '',
    requested_time: '',
    reason: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, appointmentsRes, doctorsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/appointments`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/doctors`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setAppointments(appointmentsRes.data);
      setDoctors(doctorsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/appointments`, formData, { withCredentials: true });
      toast.success('Appointment requested successfully');
      setShowForm(false);
      setFormData({ doctor_id: '', requested_date: '', requested_time: '', reason: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to request appointment');
    }
  };

  const handleApproval = async (id, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/appointments/${id}`,
        { status },
        { withCredentials: true }
      );
      toast.success(`Appointment ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="appointments-page">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                data-testid="back-btn"
                onClick={() => navigate(user?.role === 'doctor' ? '/doctor-dashboard' : '/patient-dashboard')}
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-primary" />
                <h1 className="font-heading text-xl font-semibold text-text-primary">Appointments</h1>
              </div>
            </div>
            {user?.role === 'patient' && (
              <button
                data-testid="new-appointment-btn"
                onClick={() => setShowForm(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                New Request
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* New Appointment Form */}
        {showForm && user?.role === 'patient' && (
          <div className="card-default mb-6" data-testid="appointment-form">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Request New Appointment</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-text-primary font-body font-medium mb-2">Doctor</label>
                <select
                  data-testid="doctor-select"
                  value={formData.doctor_id}
                  onChange={(e) => setFormData({ ...formData, doctor_id: e.target.value })}
                  className="w-full bg-background-subtle border-transparent focus:border-primary focus:ring-1 focus:ring-primary rounded-lg h-12 px-4 transition-all"
                  required
                >
                  <option value="">Select a doctor</option>
                  {doctors.map((doctor) => (
                    <option key={doctor.doctor_id} value={doctor.doctor_id}>
                      Dr. {doctor.user_id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-text-primary font-body font-medium mb-2">Preferred Date</label>
                  <input
                    data-testid="date-input"
                    type="date"
                    value={formData.requested_date}
                    onChange={(e) => setFormData({ ...formData, requested_date: e.target.value })}
                    className="w-full bg-background-subtle border-transparent focus:border-primary focus:ring-1 focus:ring-primary rounded-lg h-12 px-4 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-text-primary font-body font-medium mb-2">Preferred Time</label>
                  <input
                    data-testid="time-input"
                    type="time"
                    value={formData.requested_time}
                    onChange={(e) => setFormData({ ...formData, requested_time: e.target.value })}
                    className="w-full bg-background-subtle border-transparent focus:border-primary focus:ring-1 focus:ring-primary rounded-lg h-12 px-4 transition-all"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-text-primary font-body font-medium mb-2">Reason for Visit</label>
                <textarea
                  data-testid="reason-input"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full bg-background-subtle border-transparent focus:border-primary focus:ring-1 focus:ring-primary rounded-lg px-4 py-3 transition-all"
                  rows={4}
                  required
                  placeholder="Describe your symptoms or reason for appointment..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="submit-appointment-btn"
                  type="submit"
                  className="btn-primary"
                >
                  Submit Request
                </button>
                <button
                  data-testid="cancel-form-btn"
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Appointments List */}
        <div className="space-y-4" data-testid="appointments-list">
          {appointments.length === 0 ? (
            <div className="card-default text-center py-12">
              <Calendar className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <p className="text-text-secondary font-body text-lg">No appointments found</p>
              {user?.role === 'patient' && (
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-4 btn-primary"
                >
                  Request Your First Appointment
                </button>
              )}
            </div>
          ) : (
            appointments.map((appointment) => (
              <div key={appointment.appointment_id} className="card-default" data-testid={`appointment-card-${appointment.appointment_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-body font-semibold text-text-primary text-lg">{appointment.reason}</h3>
                    <p className="text-text-secondary font-body mt-1">
                      {appointment.requested_date} at {appointment.requested_time}
                    </p>
                  </div>
                  <span className={`status-badge status-${appointment.status}`}>
                    {appointment.status}
                  </span>
                </div>
                {appointment.notes && (
                  <div className="bg-background-subtle rounded-lg p-3 mb-4">
                    <p className="text-text-secondary font-body text-sm"><strong>Notes:</strong> {appointment.notes}</p>
                  </div>
                )}
                {user?.role === 'doctor' && appointment.status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      data-testid={`approve-btn-${appointment.appointment_id}`}
                      onClick={() => handleApproval(appointment.appointment_id, 'approved')}
                      className="flex-1 bg-status-success text-white rounded-lg px-4 py-2 font-medium hover:opacity-90 transition-opacity"
                    >
                      Approve
                    </button>
                    <button
                      data-testid={`deny-btn-${appointment.appointment_id}`}
                      onClick={() => handleApproval(appointment.appointment_id, 'denied')}
                      className="flex-1 bg-status-error text-white rounded-lg px-4 py-2 font-medium hover:opacity-90 transition-opacity"
                    >
                      Deny
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}